'use client'

import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Checkbox from '@mui/material/Checkbox'
import Chip from '@mui/material/Chip'
import FilterAlt from '@mui/icons-material/FilterAlt'
import IconButton from '@mui/material/IconButton'
import ListItemText from '@mui/material/ListItemText'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import Stack from '@mui/material/Stack'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Tooltip from '@mui/material/Tooltip'
import React, {useState, useMemo, useEffect} from 'react'

export function summarizeList(contentList: string[], cutOffPoint: number | null): [string, string] {
  if (cutOffPoint === null || contentList.length <= cutOffPoint) {
    return [contentList.join(', '), '']
  } else {
    return [contentList.slice(0, cutOffPoint).join(', '), `+${contentList.length - cutOffPoint}`]
  }
}

export type DynamicTableColumnAlignment = 'left' | 'center' | 'right'
export type DynamicTableColumnSize = 'xxs' | 'xs' | 's' | 'm' | 'l'

export type DynamicTableColumn<T> = {
  label: string //column header label
  field: keyof T //key of the data object
  size?: string
  align?: DynamicTableColumnAlignment
  filterable?: boolean //enables filter UI
  render?: (row: T) => React.ReactNode //optional custom cell render
  filterRender?: (value: any) => React.ReactNode //optional filter renderer (dropdown and chip)
  defaultFilterValues?: unknown[]
}

type DynamicTableProps<T> = {
  columns: DynamicTableColumn<T>[]
  data: T[]
  getRowId: (row: T) => string | number
}

export function DynamicTable<T>({columns, data, getRowId}: DynamicTableProps<T>) {
  const [filters, setFilters] = useState<Partial<Record<keyof T, Set<unknown>>>>(() => ({} as Partial<Record<keyof T, Set<unknown>>>))

  const [filterAnchor, setFilterAnchor] = useState<HTMLElement | null>(null)
  const [activeFilterField, setActiveFilterField] = useState<keyof T | null>(null)

  useEffect(() => {
    columns.map(col => {
      if (col.defaultFilterValues) {
        col.defaultFilterValues.map(val => toggleFilterValue(col.field, val, true))
      }
    })
  }, [columns])

  const filteredData = useMemo(() => {
    return data.filter((row) =>
      (Object.entries(filters)as [keyof T, Set<unknown>][]).every(([field, values]) => {
        if (!values || values.size === 0) return true
        return values.has(row[field as keyof T])
      }))
  }, [data, filters])

  const toggleFilterValue = (field: keyof T, value: unknown, only_add: boolean = false
  ) => {
    setFilters((prev) => {
      const next = new Set(prev[field] ?? [])
      next.has(value) && !only_add ? next.delete(value) : next.add(value)
      return {...prev, [field]: next}
    })
  }

  const removeFilterValue = (field: keyof T, value: unknown) => {
    setFilters((prev) => {
      const next = new Set(prev[field] ?? [])
      next.delete(value)
      return {...prev, [field]: next}
    })
  }

  const openMenu = (field: keyof T, anchor: HTMLElement) => {
    setActiveFilterField(field)
    setFilterAnchor(anchor)
  }

  const closeMenu = () => {
    setActiveFilterField(null)
    setFilterAnchor(null)
  }

  const renderCellValue = (col: DynamicTableColumn<T>, row: T) => {
    return col.render ? col.render(row) : String(row[col.field] ?? '')
  }

  const renderFilterValue = (col: DynamicTableColumn<T>, value: any) => {
    return col.filterRender ? col.filterRender(value) : col.render ? col.render(value) : String(value)
  }

  const uniqueValuesWithRow = (col: DynamicTableColumn<T>) => {
    const map = new Map<unknown, T>()
    data.forEach((row) => {
      const value = row[col.field]
      if (!map.has(value)) map.set(value, row)
    })
    return map
  }

  return (
    <TableContainer>
      <Table size='small'>
        <TableHead>
          <TableRow>
            {columns.map((col) => {
              const uniqueValueMap = uniqueValuesWithRow(col)
              const selectedValues = Array.from(filters[col.field] ?? [])

              return (
                <TableCell key={String(col.field)} align={col.align ?? 'left'} style={{width: col.size}}>
                  <Box display='flex' flexDirection='column' gap={0.5}>
                    <Box display='flex' alignItems='center' gap={0.5}>
                      <span className='font-bold text-lg'>{col.label}</span>
                      {col.filterable && (
                        <>
                          <Tooltip title='Filter'>
                            <IconButton onClick={(e) => openMenu(col.field, e.currentTarget)}>
                              <FilterAlt color={'primary'} />
                            </IconButton>
                          </Tooltip>

                          <Menu
                            anchorEl={filterAnchor}
                            open={activeFilterField === col.field}
                            onClose={closeMenu}
                            elevation={0}
                            anchorOrigin={{
                              vertical: 'bottom',
                              horizontal: 'right'
                            }}
                            transformOrigin={{
                              vertical: 'top',
                              horizontal: 'right'
                            }}
                            slotProps={{
                              paper: {
                                sx: {
                                  border: '1px solid',
                                  borderColor: 'divider',
                                  borderRadius: 6,
                                }
                              }
                            }}>
                            {Array.from(uniqueValueMap.entries()).map(([value, row], idx) => (
                              <MenuItem key={idx} onClick={() => toggleFilterValue(col.field, value)} sx={{pl: 0, pr: 1}}>
                                <Checkbox checked={filters[col.field]?.has(value) ?? false}/>
                                <ListItemText primary={renderFilterValue(col, value)}/>
                              </MenuItem>
                            ))}
                          </Menu>
                        </>
                      )}
                    </Box>
                    {selectedValues.length > 0 && (
                      <Box display='flex' flexWrap='wrap' gap={0.5}>
                        {selectedValues.map((value, idx) => {
                          const row = uniqueValueMap.get(value)
                          return (
                            <Chip key={idx} size='small' label={renderFilterValue(col, value)} color={'primary'} onDelete={() => removeFilterValue(col.field, value)}/>
                          )})}
                      </Box>
                    )}
                  </Box>
                </TableCell>
              )
            })}
          </TableRow>
        </TableHead>
        <TableBody>
          { filteredData.length > 0
            ? filteredData.map((row) => (
              <TableRow key={getRowId(row)}>
                {columns.map((col) => (
                  <TableCell key={String(col.field)} align={col.align ?? 'left'}>
                    {renderCellValue(col, row)}
                  </TableCell>
                ))}
              </TableRow>
            ))
            : (
              <TableRow>
                <TableCell colSpan={columns.length}>
                  <Stack sx={{width: '100%'}} spacing={2}>
                    <Alert severity='info' variant='outlined'>No evaluations available (pay attention to set filters)</Alert>
                  </Stack>
                </TableCell>
              </TableRow>
            )
          }
        </TableBody>
      </Table>
    </TableContainer>
  )
}
